import React, { useState, useEffect, useCallback } from "react";
import api from "../api";
import { Link } from "react-router-dom";
import { MessageCircle, Wallet, ListChecks, ArrowUpRight, Loader2, Phone } from "lucide-react";

const TZ_BERLIN = "Europe/Berlin";

function fmtDuration(minutes) {
    if (!minutes && minutes !== 0) return "—";
    const h = Math.floor(minutes / 60);
    const m = Math.floor(minutes % 60);
    return `${h}h ${m}m`;
}

/**
 * Compact dashboard widgets row — one card per major module.
 * The Stechuhr/Zeiterfassung is rendered as a much larger hero above this row
 * (see <TimeClockHero/>).  This row focuses on the secondary modules.
 */
export default function DashboardWidgets({ lang = "de", myTasksOpenCount = 0, myTodosOpenCount = 0 }) {
    const [bk, setBK] = useState({ loading: true, stats: null });
    const [sc, setSC] = useState({ loading: true, open: 0 });
    const [wa, setWA] = useState({ loading: true, summary: null });

    const load = useCallback(async () => {
        try {
            const [b, c, d] = await Promise.all([
                api.get("/invoices/stats").catch(() => ({ data: null })),
                api.get("/superchat/conversations?limit=100&status=open").catch(() => ({ data: { data: { results: [] } } })),
                api.get("/wa/summary").catch(() => ({ data: null })),
            ]);
            setBK({ loading: false, stats: b.data || null });
            const items = c.data?.data?.results || [];
            setSC({ loading: false, open: items.length });
            setWA({ loading: false, summary: d.data || null });
        } catch {
            setBK({ loading: false, stats: null });
            setSC({ loading: false, open: 0 });
            setWA({ loading: false, summary: null });
        }
    }, []);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        const h = () => load();
        window.addEventListener("mytacs:tick", h);
        return () => window.removeEventListener("mytacs:tick", h);
    }, [load]);

    return (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="dashboard-widgets">
            {/* Meine Aufgaben (private + zugewiesene) */}
            <WidgetCard
                to="/"
                icon={<ListChecks className="w-4 h-4" />}
                title={lang === "de" ? "Meine Aufgaben" : "My tasks"}
                testId="widget-mytasks"
                accent={myTasksOpenCount > 0 ? "bg-emerald-50 border-emerald-200" : "bg-white border-neutral-200"}
            >
                <p className="text-2xl font-bold tracking-tight">{myTasksOpenCount}</p>
                <p className="text-[11px] text-neutral-500 mt-0.5">
                    {lang === "de" ? "Privat & zugewiesen" : "Private & assigned"}
                    {myTodosOpenCount > 0 ? ` · ${myTodosOpenCount} ToDos` : ""}
                </p>
            </WidgetCard>

            {/* Banking */}
            <WidgetCard
                to="/banking"
                icon={<Wallet className="w-4 h-4" />}
                title="Banking"
                testId="widget-banking"
                accent={(bk.stats?.open || 0) > 0 ? "bg-blue-50 border-blue-200" : "bg-white border-neutral-200"}
            >
                {bk.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                ) : (
                    <>
                        <p className="text-2xl font-bold tracking-tight">{bk.stats?.open || 0}</p>
                        <p className="text-[11px] text-neutral-500 mt-0.5">
                            {lang === "de" ? "Offen" : "Open"}
                            {bk.stats?.sum_open ? ` · ${Number(bk.stats.sum_open).toLocaleString("de-DE", { maximumFractionDigits: 0 })} €` : ""}
                        </p>
                    </>
                )}
            </WidgetCard>

            {/* Superchat */}
            <WidgetCard
                to="/superchat"
                icon={<MessageCircle className="w-4 h-4" />}
                title="Superchat"
                testId="widget-superchat"
                accent={sc.open > 0 ? "bg-purple-50 border-purple-200" : "bg-white border-neutral-200"}
            >
                {sc.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                ) : (
                    <>
                        <p className="text-2xl font-bold tracking-tight">{sc.open}</p>
                        <p className="text-[11px] text-neutral-500 mt-0.5">{lang === "de" ? "Offene Konversationen" : "Open conversations"}</p>
                    </>
                )}
            </WidgetCard>

            {/* Business WhatsApp */}
            <WidgetCard
                to="/whatsapp"
                icon={<Phone className="w-4 h-4" />}
                title="Business WhatsApp"
                testId="widget-whatsapp"
                accent={(wa.summary?.unread_total || 0) > 0 ? "bg-emerald-50 border-emerald-200" : "bg-white border-neutral-200"}
            >
                {wa.loading ? (
                    <Loader2 className="w-4 h-4 animate-spin text-neutral-400" />
                ) : (
                    <>
                        <p className="text-2xl font-bold tracking-tight text-emerald-700">{wa.summary?.unread_total || 0}</p>
                        <p className="text-[11px] text-neutral-500 mt-0.5">
                            {lang === "de" ? "Ungelesen" : "Unread"}
                            {wa.summary?.auto_tasks_open ? ` · ${wa.summary.auto_tasks_open} ${lang === "de" ? "Auto-Tasks" : "auto-tasks"}` : ""}
                        </p>
                    </>
                )}
            </WidgetCard>
        </div>
    );
}

function WidgetCard({ to, icon, title, accent, testId, children }) {
    return (
        <Link
            to={to}
            className={`rounded-lg border p-3 hover:shadow-md transition group ${accent}`}
            data-testid={testId}
        >
            <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] font-semibold uppercase tracking-[0.15em] text-neutral-500 flex items-center gap-1.5">
                    {icon}
                    {title}
                </span>
                <ArrowUpRight className="w-3.5 h-3.5 text-neutral-300 group-hover:text-neutral-700 transition" />
            </div>
            {children}
        </Link>
    );
}
